import request from 'lib/request';
import type {
  AgentConfig,
  AgentRunsResponse,
  DurationBucket,
  ProcessDetail,
  RunDetail,
  RunsQuery,
} from './types';

export interface HistogramFilters {
  status: string | null;
  model: string | null;
  start: string | null;
  end: string | null;
}

// `request.get` returns `{ error: 'AbortError' }` when its AbortController fires;
// the fetch wrappers re-throw it as `new Error('AbortError')`. Callers use this
// to swallow aborts instead of surfacing them as a load failure.
export const isAbortError = (e: unknown): boolean =>
  e instanceof Error && e.message === 'AbortError';

// `request.get<T>` resolves to `T | { error: string }`. Every fetcher unwraps
// the same way: re-throw the error envelope as an `Error` (so aborts surface as
// `new Error('AbortError')` for `isAbortError`), otherwise return the payload.
const unwrap = <T>(response: T | { error: string }): T => {
  if (response && typeof response === 'object' && 'error' in response) {
    throw new Error((response as { error: string }).error);
  }
  return response as T;
};

export const fetchAgentRuns = async (
  agentType: string,
  query: RunsQuery,
  abortController?: AbortController
): Promise<AgentRunsResponse> => {
  const params = new URLSearchParams({
    page: String(query.page),
    per_page: String(query.pageSize),
    dir: query.dir,
  });
  if (query.sort) params.set('sort', query.sort);
  if (query.status) params.set('status', query.status);
  if (query.model) params.set('model', query.model);
  if (query.start) params.set('start', query.start);
  if (query.end) params.set('end', query.end);
  if (query.minDuration !== null) {
    params.set('min_duration', String(query.minDuration));
  }
  if (query.maxDuration !== null) {
    params.set('max_duration', String(query.maxDuration));
  }

  const response = await request.get<AgentRunsResponse>(
    `/admin/agent_costs/agents/${encodeURIComponent(
      agentType
    )}?${params.toString()}`,
    abortController
  );
  return unwrap(response);
};

export const fetchRunDetail = async (
  runId: number,
  abortController?: AbortController
): Promise<RunDetail> => {
  const response = await request.get<RunDetail>(
    `/admin/agent_costs/runs/${runId}`,
    abortController
  );
  return unwrap(response);
};

export const fetchProcessDetail = async (
  processId: number,
  abortController?: AbortController
): Promise<ProcessDetail> => {
  const response = await request.get<ProcessDetail>(
    `/admin/agent_costs/processes/${processId}`,
    abortController
  );
  return unwrap(response);
};

export const fetchAgentConfig = async (
  agentType: string,
  abortController?: AbortController
): Promise<AgentConfig> => {
  const response = await request.get<AgentConfig>(
    `/admin/agent_costs/agents/${encodeURIComponent(agentType)}/config`,
    abortController
  );
  return unwrap(response);
};

export const fetchDurationHistogram = async (
  agentType: string,
  filters: HistogramFilters,
  abortController?: AbortController
): Promise<{ data: DurationBucket[] }> => {
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.model) params.set('model', filters.model);
  if (filters.start) params.set('start', filters.start);
  if (filters.end) params.set('end', filters.end);

  const response = await request.get<{ data: DurationBucket[] }>(
    `/admin/agent_costs/agents/${encodeURIComponent(
      agentType
    )}/duration_histogram?${params.toString()}`,
    abortController
  );
  return unwrap(response);
};
